// Admin UI pro OAuth klienty registrované přes DCR.
//
// Klienti se vytváří automaticky když ChatGPT (a další MCP klienti) projdou
// `/oauth/register` (Dynamic Client Registration, RFC 7591). Tady je můžeme:
//  - vidět v tabulce (kdo se zaregistroval, kdy, jakou cenovou úroveň má)
//  - změnit cenovou úroveň (velko / malo)
//  - zneplatnit (odebere přístup, ale ponechá v evidenci)
//  - smazat (úplně odstraní + invaliduje tokeny)
//
// Vystavené access tokeny si nesou tier z času jejich vystavení – změna tieru
// klienta se projeví až u dalších nově vystavených tokenů (po novém OAuth flow).

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3001/api'
  : '/api';

const TIER_LABELS = {
  velko: 'Velkoobchodní',
  malo: 'Maloobchodní',
};

function notify(message, type = 'success') {
  if (typeof window.showNotification === 'function') {
    window.showNotification(message, type);
  } else {
    console.log(`[${type}] ${message}`);
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('cs-CZ') + ' ' + d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export class OAuthClientsAdmin {
  constructor() {
    this.listEl = document.getElementById('oauth-clients-list');
    this.editModal = document.getElementById('oauth-client-edit-modal');
    this.clients = [];

    if (!this.listEl) return;
    this.bindEvents();
  }

  bindEvents() {
    document.getElementById('oauth-client-edit-cancel').addEventListener('click', () => this.closeEditModal());
    document.getElementById('oauth-client-edit-submit').addEventListener('click', () => this.submitEdit());
    this.editModal.addEventListener('click', (e) => { if (e.target === this.editModal) this.closeEditModal(); });

    this.listEl.addEventListener('click', (e) => {
      const editBtn = e.target.closest('[data-action="edit-oauth"]');
      if (editBtn) {
        this.openEditModal(editBtn.getAttribute('data-id'));
        return;
      }
      const revokeBtn = e.target.closest('[data-action="revoke-oauth"]');
      if (revokeBtn) {
        const id = revokeBtn.getAttribute('data-id');
        const name = revokeBtn.getAttribute('data-name');
        if (confirm(`Zneplatnit klienta "${name}"? Všechny jeho aktivní tokeny budou okamžitě zrušeny – uživatelé se musí v ChatGPT znovu přihlásit.`)) {
          this.revoke(id);
        }
        return;
      }
      const deleteBtn = e.target.closest('[data-action="delete-oauth"]');
      if (deleteBtn) {
        const id = deleteBtn.getAttribute('data-id');
        const name = deleteBtn.getAttribute('data-name');
        if (confirm(`Trvale smazat klienta "${name}" z evidence? Zruší se i všechny tokeny.`)) {
          this.delete(id);
        }
      }
    });
  }

  async load() {
    try {
      const res = await fetch(`${API_BASE_URL}/oauth-admin/clients`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Neznámá chyba');
      this.clients = data.data || [];
      this.render();
    } catch (err) {
      console.error('Chyba při načítání OAuth klientů:', err);
      this.listEl.innerHTML = `<div class="p-6 text-sm text-red-600 text-center">Chyba: ${escapeHtml(err.message)}</div>`;
    }
  }

  render() {
    if (!this.clients.length) {
      this.listEl.innerHTML = `
        <div class="p-6 text-sm text-gray-500 text-center">
          Zatím žádní OAuth klienti.<br>
          <span class="text-xs">Klient se objeví, když si ho zaregistruje ChatGPT „Nová aplikace BETA" přes DCR endpoint.</span>
        </div>`;
      return;
    }

    const rows = this.clients.map((c) => {
      const tier = TIER_LABELS[c.pricing_tier] || c.pricing_tier;
      const tierBadge = c.pricing_tier === 'malo'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-purple-100 text-purple-800';
      const statusBadge = c.revoked_at
        ? '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Odvolaný</span>'
        : '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Aktivní</span>';
      const isActive = !c.revoked_at;
      const software = c.software_id ? `<div class="text-xs text-gray-500">Software: <code>${escapeHtml(c.software_id)}</code></div>` : '';
      const redirects = (c.redirect_uris || []).map(u => `<code class="text-xs">${escapeHtml(u)}</code>`).join('<br>');
      return `
        <tr class="border-t border-gray-100">
          <td class="px-4 py-3 text-sm align-top">
            <div class="font-medium text-ink">${escapeHtml(c.client_name)}</div>
            <div class="text-xs text-gray-500 font-mono break-all">${escapeHtml(c.client_id)}</div>
            ${software}
          </td>
          <td class="px-4 py-3 text-sm align-top">
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tierBadge}">${escapeHtml(tier)}</span>
          </td>
          <td class="px-4 py-3 text-sm align-top">${statusBadge}</td>
          <td class="px-4 py-3 text-xs text-gray-600 align-top">
            <div>Registrace: ${formatDate(c.created_at)}</div>
            <div class="mt-1">Redirect URI:<br>${redirects || '<em>—</em>'}</div>
          </td>
          <td class="px-4 py-3 text-sm text-right whitespace-nowrap align-top">
            ${isActive ? `<button data-action="edit-oauth" data-id="${escapeHtml(c.client_id)}" class="btn btn-ghost btn-sm text-blue-700 hover:bg-blue-50">Upravit</button>` : ''}
            ${isActive ? `<button data-action="revoke-oauth" data-id="${escapeHtml(c.client_id)}" data-name="${escapeHtml(c.client_name)}" class="btn btn-ghost btn-sm text-yellow-700 hover:bg-yellow-50 ml-1">Zneplatnit</button>` : ''}
            <button data-action="delete-oauth" data-id="${escapeHtml(c.client_id)}" data-name="${escapeHtml(c.client_name)}" class="btn btn-ghost btn-sm text-red-700 hover:bg-red-50 ml-1">Smazat</button>
          </td>
        </tr>`;
    }).join('');

    this.listEl.innerHTML = `
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Klient</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detaily</th>
            <th class="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  openEditModal(clientId) {
    const c = this.clients.find((x) => x.client_id === clientId);
    if (!c) {
      notify('Klient nenalezen, obnov stránku', 'warning');
      return;
    }
    document.getElementById('oauth-client-edit-id').value = c.client_id;
    document.getElementById('oauth-client-edit-name').value = c.client_name || '';
    document.getElementById('oauth-client-edit-tier').value = c.pricing_tier || 'velko';
    this.editModal.classList.remove('hidden');
    setTimeout(() => document.getElementById('oauth-client-edit-name').focus(), 50);
  }

  closeEditModal() {
    this.editModal.classList.add('hidden');
  }

  async submitEdit() {
    const id = document.getElementById('oauth-client-edit-id').value;
    const client_name = document.getElementById('oauth-client-edit-name').value.trim();
    const pricing_tier = document.getElementById('oauth-client-edit-tier').value;
    if (!id) return;

    try {
      const res = await fetch(`${API_BASE_URL}/oauth-admin/clients/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_name, pricing_tier }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      this.closeEditModal();
      notify('OAuth klient upraven', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při úpravě OAuth klienta:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }

  async revoke(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/oauth-admin/clients/${encodeURIComponent(id)}/revoke`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      notify('Klient zneplatněn, tokeny zrušeny', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při zneplatnění OAuth klienta:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }

  async delete(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/oauth-admin/clients/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      notify('Klient smazán', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při mazání OAuth klienta:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }
}
